import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose, bindActionCreators } from 'redux';

import injectSaga from 'utils/injectSaga';

import { withRouter } from 'react-router';

import CapSkeleton from '@capillarytech/cap-ui-library/CapSkeleton';
import multipleOrgSwitch from '@capillarytech/cap-ui-utils/utils/multipleOrgSwitch';
import { GA } from '@capillarytech/cap-ui-utils';

import sagas from './saga';
import * as actions from './actions';

import componentRoutes from './routes';
import * as constants from './constants';
import * as appConstants from '../App/constants';
import * as selectors from './selectors';
import messages from '../SomethingWentWrong/messages';

import { matchOrgChangeHandler } from '../../organisms/OrgChange/orgChangeRegistry';

const NavigationBar = React.lazy(() => import('../../organisms/NavigationBar'));
const OrgChange = React.lazy(() => import('../../organisms/OrgChange'));
const CapSomethingWentWrong = React.lazy(() =>
  import('@capillarytech/cap-ui-library/CapSomethingWentWrong'),
);

const { REQUEST, SUCCESS, FAILURE, IDLE } = appConstants;
const {
  makeSelectOrg,
  makeSelectCap,
  makeSelectSidebarMenuData,
  makeSelectBeamerPostsNotificationData,
} = selectors;
const {
  HELP_URL,
  SIDEBAR_MENU_ITEM_POSITION,
} = constants;

const { isMultipleTabsOpen } = multipleOrgSwitch;

const gtm = window.dataLayer_host || [];

// Page-shaped placeholder — skeleton everywhere instead of spinners, so the
// boot experience matches the MFE outlet skeleton.
const CapPageSkeleton = () => (
  <div style={{ padding: 24 }}>
    <CapSkeleton active title paragraph={{ rows: 2 }} />
    <CapSkeleton active title={false} paragraph={{ rows: 9 }} />
  </div>
);

export const Cap = ({
  sidebarMenuData,
  history,
  actions,
  userData,
  orgData,
  intl: { formatMessage },
  beamerNotificationData: {
    getBeamerPostsNotificationStatus = '',
    beamerPostsNotification = [],
  } = {},
}) => {
  const { pathname } = location;
  const { orgID, fetchingUserdata } = orgData;
  const { user, currentOrgDetails = {} } = userData;
  const { refID } = user;
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  GA.initialize({
    accessKey: constants.GA_TRACKING_ID,
    trackUIError: true,
    trackLoadPerformance: 'Infinity-UI',
  });

  // Auth.initialize + window.capAuth are now set once from the user-info saga via
  // utils/capAuthBridge (publishCapAuth), so the full auth-response superset is
  // published in a single place before remotes consume it. Doing it here in a
  // useEffect would re-freeze window.capAuth back to the basic shape and drop the
  // superset.
  useEffect(
    () => {
      const getUserGtmData = userData => {
        const userName = userData?.attributes?.USERNAME;
        const userEmail = userData?.attributes?.EMAIL;
        const orgObj = userData?.proxyOrgList?.find(org => org.orgID === orgID);
        const gtmData = {
          orgID,
          orgName: orgObj?.orgName,
          userId: userData?.refID,
          userName: userName?.value,
          userEmail: userEmail?.value,
          isCapUser: userData?.isCapUser,
        };
        return gtmData;
      };
      if (refID !== undefined) {
        const userGtmData = getUserGtmData(user);
        gtm.push({ ...userGtmData, event: 'userAuthenticated' });
      }
      if (orgID !== undefined) {
        gtm.push({ orgID });
      }
    },
    [refID, orgID],
  );

  useEffect(
    () => {
      actions.getLastSyncTime();
      if (!fetchingUserdata) {
        actions.getUserData();
        actions.getBeamerPostsExplore();
        actions.getBeamerPostsNotification();
      }
      return () => {
        actions.clearTopbarMenuData();
      };
    },
    [actions],
  );

  useEffect(
    () => {
      if (fetchingUserdata == IDLE) {
        //variable might have slightly different name in different apps
        actions.getUserData();
      }
      return () => {
        actions.clearTopbarMenuData();
      };
    },
    [actions],
  );

  const changeOrg = orgId => {
    if (isMultipleTabsOpen()) {
      setSelectedOrgId(orgId);
    } else {
      changeOrgAction(orgId);
    }
  };

  const changeOrgAction = orgId => {
    actions.changeOrg(orgId, navigateToDashboard);
  };

  const navigateToDashboard = matchOrgChangeHandler(pathname);

  const logout = () => {
    actions.logout();
  };

  // Kept dormant while v1 and v2 login pages coexist. To flip the top-bar
  // logout back to the v2 flow once v1 is deprecated, change the
  // NavigationBar's `logout` prop below back to:
  //   logout={process.env.NODE_ENV === PRODUCTION ? logoutV2 : logout}
  const logoutV2 = () => {
    actions.logoutV2();
  };

  const onClickReload = () => {
    window.location.reload();
  };

  return (
    <div className="cap-container">
      {fetchingUserdata === REQUEST && <CapPageSkeleton />}
      {fetchingUserdata === SUCCESS && (
        <React.Suspense fallback={<CapPageSkeleton />}>
          <NavigationBar
            userData={userData}
            componentRoutes={componentRoutes}
            topbarMenuData={[]}
            sidebarMenuItemsPosition={SIDEBAR_MENU_ITEM_POSITION}
            helpUrl={HELP_URL}
            changeOrg={changeOrg}
            history={history}
            sidebarMenuData={sidebarMenuData}
            logout={logout}
            pathname={pathname}
            beamerPostsNotification={beamerPostsNotification}
            getBeamerPostsNotificationStatus={getBeamerPostsNotificationStatus}
            isNotificationEnabled={currentOrgDetails?.accessibleFeatures?.includes(
              'ENABLE_NOTIFICATION_CENTRE',
            )}
          />
        </React.Suspense>
      )}
      {fetchingUserdata === FAILURE && (
        <React.Suspense fallback={<CapPageSkeleton />}>
          <CapSomethingWentWrong
            title={formatMessage(messages.somethingWentWrongTitle)}
            description={formatMessage(messages.somethingWentWrongDesc)}
            reloadText={formatMessage(messages.tryRefreshing)}
            onClickReload={onClickReload}
          />
        </React.Suspense>
      )}
      {/* Modal chunk — nothing to show while it loads */}
      <React.Suspense fallback={null}>
        <OrgChange
          userData={userData}
          selectedOrgId={selectedOrgId}
          changeOrgAction={changeOrgAction}
          navigateToDashboard={navigateToDashboard}
          onModalClose={setSelectedOrgId}
        />
      </React.Suspense>
    </div>
  );
};

Cap.propTypes = {
  history: PropTypes.object,
  orgData: PropTypes.object,
  userData: PropTypes.object,
  sidebarMenuData: PropTypes.array,
  actions: PropTypes.object.isRequired,
  intl: intlShape.isRequired,
  beamerNotificationData: PropTypes.object,
};

Cap.defaultProps = {};

const mapStateToProps = createStructuredSelector({
  orgData: makeSelectOrg(),
  userData: makeSelectCap(),
  sidebarMenuData: makeSelectSidebarMenuData(),
  beamerNotificationData: makeSelectBeamerPostsNotificationData(),
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(actions, dispatch),
});

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

const withSaga = sagas.map((saga, index) =>
  injectSaga({ key: `cap-${index}`, saga }),
);

export default compose.apply(null, [...withSaga, withConnect])(
  withRouter(injectIntl(Cap)),
);
